import React, { useState, useMemo } from "react";
import { printB2BDocument, printBonDeLivraison, printEtiquette } from "../../data/b2bDocuments";
import { deleteB2BSale, deductStockForFacture, cloneSaleForRepeat, validateB2BFacture } from "../../data/b2bSales";
import FactureEditorModal from "./FactureEditorModal";
import FactureViewModal from "./FactureViewModal";

export default function B2BSalesList({ sales, setSales, clients, catalogProducts, taxRates, onRequestPayment }) {
  const [editorSale, setEditorSale] = useState(null);
  const [viewSale, setViewSale] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");

  const handlePrintDocument = (sale, docType) => {
    const client = clients.find(c => c.id === sale.clientId) || {};
    if (docType === "facture") printB2BDocument(sale, client);
    else if (docType === "bl") printBonDeLivraison(sale, client);
    else if (docType === "etiquette") printEtiquette(sale, client);
  };

  const handleDeliver = async (sale) => {
    if (!window.confirm("Confirmer la livraison ? Cela déduira le stock pour cette facture.")) return;
    try {
      const updated = await deductStockForFacture(sale);
      setSales(prev => prev.map(s => s.id === updated.id ? updated : s));
    } catch (err) {
      alert("Erreur lors de la livraison : " + err.message);
    }
  };

  const handleRepeat = (sale) => {
    const cloned = cloneSaleForRepeat(sale, catalogProducts);
    setEditorSale(cloned);
  };

  const handleValidate = async (sale) => {
    if (!window.confirm("Valider la facture ? Le numéro sera définitif et le document ne pourra plus être modifié ni supprimé.")) return;
    try {
      const updated = await validateB2BFacture(sale);
      setSales(prev => prev.map(s => s.id === updated.id ? updated : s));
    } catch (err) {
      alert("Erreur lors de la validation : " + err.message);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Supprimer ce document ?")) return;
    try {
      await deleteB2BSale(id);
      setSales(prev => prev.filter(s => s.id !== id));
    } catch (err) {
      alert(err.message);
    }
  };

  const handleCreateNew = () => {
    setEditorSale({
      id: "b2b-" + Date.now(),
      clientId: "",
      documentType: "facture",
      documentNumber: "",
      status: "pending",
      validated: false,
      validatedAt: null,
      deliveryAddressId: "",
      deliveryAddressText: "",
      expectedDate: null,
      notes: "",
      items: [],
      createdAt: new Date().toISOString()
    });
  };

  const handleSave = (saved) => {
    setSales((prev) => {
      const idx = prev.findIndex(s => s.id === saved.id);
      return idx >= 0 ? prev.map(s => s.id === saved.id ? saved : s) : [saved, ...prev];
    });
  };

  const getClientName = (clientId) => {
    const c = clients.find(x => x.id === clientId);
    return c ? c.companyName || c.name : "";
  };

  const query = searchQuery.trim().toLowerCase();
  const filteredSales = useMemo(() => {
    if (!query) return sales;
    return (sales || []).filter(s => getClientName(s.clientId).toLowerCase().includes(query));
  }, [query, sales, clients]);

  const selectedClientId = useMemo(() => {
    if (!query) return null;
    const exact = clients.find(c => (c.companyName || "").toLowerCase() === query);
    if (exact) return exact.id;
    const single = clients.filter(c => (c.companyName || "").toLowerCase().includes(query));
    return single.length === 1 ? single[0].id : null;
  }, [query, clients]);

  const historyClient = clients.find(c => c.id === selectedClientId) || null;
  const historySales = historyClient ? (sales || []).filter(s => s.clientId === historyClient.id) : [];
  const historyTotals = historySales.reduce((acc, s) => {
    acc.ht += Number(s.totalHT || 0);
    acc.tva += Number(s.totalTVA || 0);
    acc.ttc += Number(s.totalTTC || 0);
    if (s.status === "paid") acc.paid += Number(s.totalTTC || 0);
    return acc;
  }, { ht: 0, tva: 0, ttc: 0, paid: 0 });
  const totalSales = (sales || []).filter(s => s.documentType === "facture").reduce((acc, s) => acc + (s.totalTTC || 0), 0);

  return (
    <div className="mt-4">
      <div className="d-flex justify-content-between align-items-center mb-4 gap-2 flex-wrap">
        <h3 className="mb-0">Factures & Devis</h3>
        <div className="d-flex gap-2">
          <div className="position-relative">
            <input
              type="text"
              className="form-control form-control-sm"
              style={{ width: "260px" }}
              placeholder="Rechercher par client..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            {searchQuery && (
              <button type="button" className="btn btn-sm btn-link position-absolute end-0 top-0" onClick={() => setSearchQuery("")}>
                <i className="fas fa-times"></i>
              </button>
            )}
          </div>
          <button className="btn btn-primary btn-sm" onClick={handleCreateNew}>
            <i className="fas fa-plus me-2"></i>Nouveau Document
          </button>
        </div>
      </div>

      <div className="table-responsive bg-white rounded shadow-sm">
        <table className="table table-hover align-middle mb-0">
          <thead className="table-light">
            <tr>
              <th>Réf</th>
              <th>Date</th>
              <th>Type</th>
              <th>Client</th>
              <th>Total TTC</th>
              <th>Statut</th>
              <th className="text-end">Actions</th>
            </tr>
          </thead>
          <tbody>
            {(filteredSales || []).map(sale => {
              const isDevis = sale.documentType === "devis";
              const isDelivered = sale.status === "delivered" || sale.status === "paid";
              const isPaid = sale.status === "paid";
              const isValidated = isFacture(sale) && (sale.validated || isDelivered);
              const clientName = getClientName(sale.clientId);

              return (
                <tr key={sale.id}>
                  <td>
                    <strong>{isFacture(sale) && !isValidated ? "BROUILLON" : sale.documentNumber}</strong>
                  </td>
                  <td>{new Date(sale.createdAt || sale.date).toLocaleDateString("fr-FR")}</td>
                  <td>
                    <span className={`badge ${isDevis ? "bg-secondary" : "bg-primary"}`}>
                      {isDevis ? "DEVIS" : "FACTURE"}
                    </span>
                  </td>
                  <td style={{ cursor: "pointer" }} title="Voir l'historique client" onClick={() => setSearchQuery(clientName)}>
                    {clientName}
                  </td>
                  <td><strong>{(sale.totalTTC || 0).toFixed(2)} €</strong></td>
                  <td>
                    {isDevis ? (
                      <span className="badge bg-warning text-dark">{sale.status}</span>
                    ) : (
                      <>
                        {isValidated ? <span className="badge bg-success me-1">Validée</span> : <span className="badge bg-warning text-dark me-1">Brouillon</span>}
                        {isDelivered ? <span className="badge bg-info me-1">Livré</span> : null}
                        {isPaid ? <span className="badge bg-success">Payé</span> : null}
                      </>
                    )}
                  </td>
                  <td className="text-end">
                    <button className="btn btn-sm btn-outline-secondary me-1" onClick={() => setViewSale(sale)} title="Voir le document">
                      <i className="fas fa-eye"></i>
                    </button>
                    <div className="dropdown d-inline-block me-1">
                      <button className="btn btn-sm btn-outline-secondary dropdown-toggle" type="button" data-bs-toggle="dropdown">
                        <i className="fas fa-print"></i>
                      </button>
                      <ul className="dropdown-menu dropdown-menu-end">
                        <li><button className="dropdown-item" onClick={() => handlePrintDocument(sale, "facture")}><i className="fas fa-file-invoice me-2"></i>Imprimer {isDevis ? "Devis" : "Facture"}</button></li>
                        <li><button className="dropdown-item" onClick={() => handlePrintDocument(sale, "bl")}><i className="fas fa-truck me-2"></i>Bon Livraison</button></li>
                        <li><button className="dropdown-item" onClick={() => handlePrintDocument(sale, "etiquette")}><i className="fas fa-tag me-2"></i>Étiquette Colis</button></li>
                      </ul>
                    </div>
                    <button className="btn btn-sm btn-outline-primary me-1" onClick={() => handleRepeat(sale)} title="Répéter / rééditer">
                      <i className="fas fa-redo"></i>
                    </button>
                    {isFacture(sale) && !isValidated && (
                      <button className="btn btn-sm btn-success me-1" onClick={() => handleValidate(sale)} title="Valider la facture">
                        <i className="fas fa-check-circle"></i>
                      </button>
                    )}
                    {isFacture(sale) && !isDelivered && isValidated && (
                      <button className="btn btn-sm btn-outline-info me-1" onClick={() => handleDeliver(sale)} title="Marquer livré (Déduit le stock)">
                        <i className="fas fa-truck"></i>
                      </button>
                    )}
                    {isFacture(sale) && !isPaid && (
                      <button className="btn btn-sm btn-outline-success me-1" onClick={() => onRequestPayment(sale)} title="Encaisser paiement">
                        <i className="fas fa-euro-sign"></i>
                      </button>
                    )}
                    {!isFacture(sale) || !isValidated ? (
                      <button className="btn btn-sm btn-outline-danger" onClick={() => handleDelete(sale.id)}>
                        <i className="fas fa-trash"></i>
                      </button>
                    ) : null}
                  </td>
                </tr>
              );
            })}
            {(!filteredSales || filteredSales.length === 0) && (
              <tr>
                <td colSpan="7" className="text-center py-4 text-muted">
                  {searchQuery ? "Aucun document pour ce client." : "Aucune vente ou devis trouvé."}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {historyClient && (
        <div className="mt-4 p-3 bg-white rounded shadow-sm">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h5 className="mb-0">
              Historique — <strong>{historyClient.companyName}</strong>
            </h5>
            <span className="text-muted small">{historySales.length} document(s)</span>
          </div>
          <div className="row text-center mb-3">
            <div className="col-6 col-md-3">
              <div className="text-muted small">Total HT</div>
              <strong>{historyTotals.ht.toFixed(2)} €</strong>
            </div>
            <div className="col-6 col-md-3">
              <div className="text-muted small">Total TVA</div>
              <strong>{historyTotals.tva.toFixed(2)} €</strong>
            </div>
            <div className="col-6 col-md-3">
              <div className="text-muted small">Total TTC</div>
              <strong>{historyTotals.ttc.toFixed(2)} €</strong>
            </div>
            <div className="col-6 col-md-3">
              <div className="text-muted small">Encaissé / Restant à encaisser</div>
              <strong>{historyTotals.paid.toFixed(2)} € / {(historyTotals.ttc - historyTotals.paid).toFixed(2)} €</strong>
            </div>
          </div>
          <table className="table table-sm table-hover align-middle mb-0">
            <thead className="table-light">
              <tr>
                <th>Réf</th>
                <th>Date</th>
                <th>Type</th>
                <th>Total TTC</th>
                <th>Statut</th>
                <th className="text-end"></th>
              </tr>
            </thead>
            <tbody>
              {historySales.map(s => (
                <tr key={s.id}>
                  <td><strong>{isFacture(s) && !(s.validated || s.status === "delivered" || s.status === "paid") ? "BROUILLON" : s.documentNumber}</strong></td>
                  <td>{new Date(s.createdAt || s.date).toLocaleDateString("fr-FR")}</td>
                  <td><span className={`badge ${s.documentType === "devis" ? "bg-secondary" : "bg-primary"}`}>{s.documentType === "devis" ? "DEVIS" : "FACTURE"}</span></td>
                  <td><strong>{(s.totalTTC || 0).toFixed(2)} €</strong></td>
                  <td>
                    {s.status === "paid" ? <span className="badge bg-success">Payé</span>
                      : s.status === "delivered" ? <span className="badge bg-info">Livré</span>
                      : <span className="badge bg-warning text-dark">{s.documentType === "devis" ? "Devis" : "En attente"}</span>}
                  </td>
                  <td className="text-end">
                    <button className="btn btn-sm btn-outline-secondary" onClick={() => setViewSale(s)}><i className="fas fa-eye"></i></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="mt-3 text-muted">
        Total factures : <strong>{(totalSales || 0).toFixed(2)} € TTC</strong>
      </div>

      <FactureEditorModal
        sale={editorSale}
        clients={clients}
        catalogProducts={catalogProducts}
        taxRates={taxRates}
        onClose={() => setEditorSale(null)}
        onSave={handleSave}
      />
      <FactureViewModal
        sale={viewSale}
        client={clients.find(c => c.id === viewSale?.clientId)}
        onClose={() => setViewSale(null)}
      />
    </div>
  );
}

function isFacture(sale) {
  return sale && sale.documentType === "facture";
}